import React, { useState, useMemo } from 'react';
import { AlertCircle, Activity } from 'lucide-react';
import { usePreQualifiedLeads, useCallAgentLeads } from '../../hooks/useSupabaseLeads';
import AgentHeader from '../CallAgent/AgentHeader';
import TodayHighlights from '../CallAgent/TodayHighlights';
import LeadGenMilestones from './LeadGenMilestones';
import FeedbackPopup from '../FeedbackPopup';
import LeadGenActionPanel from './LeadGenActionPanel';
import LeadGenActivityFeed from './LeadGenActivityFeed';
import LeadGenNextActionsPanel from './LeadGenNextActionsPanel';
import PerformanceIntelligence from '../shared/PerformanceIntelligence';
import { useTasksQueue } from '../../hooks/useTasksQueue';
import LeadGenHowIWorkModal from './LeadGenHowIWorkModal';
import { Users, UserCheck, Target, TrendingUp, Award, Mail, Phone, Linkedin } from 'lucide-react';
import LoadingScreen from '../shared/LoadingScreen';

const LeadGenAgentPage = () => {
  const { data: allLeads, isLoading, error } = usePreQualifiedLeads();
  const { data: allTrackingLeads } = useCallAgentLeads();
  const taskQueue = useTasksQueue('leadgen');
  const [showHowIWorkModal, setShowHowIWorkModal] = useState(false);

  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const todayEnd = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  const todayLeads = useMemo(() => {
    return allLeads.filter(lead => {
      const leadDate = new Date(lead.created_at);
      return leadDate >= todayStart && leadDate <= todayEnd;
    });
  }, [allLeads, todayStart, todayEnd]);

  const todayStats = useMemo(() => {
    const leadsFound = todayLeads.length;

    const emailsFound = todayLeads.filter(lead =>
      lead.dm_email_1
    ).length;

    const phonesFound = todayLeads.filter(lead =>
      lead.dm_mobile_1
    ).length;

    const linkedInFound = todayLeads.filter(lead =>
      lead.dm_linkedin
    ).length;

    const leadsEnriched = todayLeads.filter(lead =>
      lead.enrichment_status === 'enriched'
    ).length;

    const leadsQualified = allTrackingLeads.filter(lead => {
      if (!lead.created_at) return false;
      const d = new Date(lead.created_at);
      return d >= todayStart && d <= todayEnd;
    }).length;

    return { leadsFound, emailsFound, phonesFound, linkedInFound, leadsEnriched, leadsQualified };
  }, [todayLeads, allTrackingLeads, todayStart, todayEnd]);

  const agentStatus = useMemo(() => {
    if (allLeads.length === 0) return 'idle';

    const lastLead = allLeads[0];
    const lastLeadTime = new Date(lastLead.created_at);
    const now = new Date();
    const hoursSinceLastLead = (now.getTime() - lastLeadTime.getTime()) / (1000 * 60 * 60);

    if (hoursSinceLastLead < 1) return 'active';
    return 'processing';
  }, [allLeads]);

  const totalQualifiedLeads = useMemo(() => {
    return allLeads.filter(lead => {
      const status = lead.lead_status?.toLowerCase();
      return status?.includes('ready to go') || status?.includes('looks good');
    }).length;
  }, [allLeads]);

  if (isLoading && allLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <AgentHeader
        status={agentStatus}
        title="Lead Gen Agent"
        subtitle="I find, qualify, and deliver leads ready for outreach."
        avatarSrc="https://utimppxbdeptpoyotmcx.supabase.co/storage/v1/object/public/agents-avatars/550e8400-e29b-41d4-a716-446655440001/leadgenagent.png"
        avatarAlt="Lead Gen Agent"
        activeLabel="Analyzing lead sources…"
        icon="phone"
        processingMessages={[
          'Analyzing lead sources',
          'Extracting decision makers',
          'Enriching contact data',
          'Verifying email addresses',
          'Qualifying prospects',
          'Scoring lead matches',
          'Updating CRM data',
          'Running verification checks'
        ]}
      />

      <LeadGenActionPanel
        onHowIWork={() => setShowHowIWorkModal(true)}
      />

      <div className="mt-[60px]">
        <TodayHighlights
          metrics={[
            { value: todayStats.leadsFound, label: 'Leads Found', icon: Users },
            { value: todayStats.emailsFound, label: 'Emails Found', icon: Mail },
            { value: todayStats.phonesFound, label: 'Phones Found', icon: Phone },
            { value: todayStats.linkedInFound, label: 'LinkedIn Profiles', icon: Linkedin },
            { value: todayStats.leadsEnriched, label: 'Leads Enriched', icon: UserCheck },
            { value: todayStats.leadsQualified, label: 'Leads Qualified', icon: Target },
          ]}
        />
      </div>

      <div className="space-y-3 mt-[60px]">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
            <Activity className="text-white" size={22} />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Live Operations</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ minHeight: '600px' }}>
          <LeadGenActivityFeed
            processingTasks={taskQueue.processingTasks}
            recentFinishedTasks={taskQueue.recentFinishedTasks}
            hasProcessing={taskQueue.hasProcessing}
            hasAnyTasks={taskQueue.hasAnyTasks}
          />
          <LeadGenNextActionsPanel
            pendingTasks={taskQueue.pendingTasks}
            hasAnyTasks={taskQueue.hasAnyTasks}
          />
        </div>
      </div>

      <div className="mt-[60px]">
        <PerformanceIntelligence agentType="leadgen" />
      </div>

      <div className="mt-[60px]">
        <LeadGenMilestones totalQualifiedLeads={totalQualifiedLeads} />
      </div>

      <LeadGenHowIWorkModal
        isOpen={showHowIWorkModal}
        onClose={() => setShowHowIWorkModal(false)}
      />

      <FeedbackPopup isVisible={true} />
    </div>
  );
};

export default LeadGenAgentPage;
